/**
 * Discount-code (Stripe coupon NAME, e.g. SAVE30) helpers for checkout.
 * Twin of react-marketing-site/src/lib/billing/discountCode.js — keep the
 * exported semantics in sync.
 *
 * Backend contract (billing-manager, no auth, read-only):
 *   GET {PUBLIC_BILLING_API_BASE}/public/api/v1/billing/discount-code?code=SAVE30
 *   200 {"valid":true,"code":"SAVE30","percent_off":30,"amount_off":0,
 *        "currency":"","duration":"once"}
 *   200 {"valid":false}
 * Product-scoped coupons additionally carry "applies_to_products" (Stripe
 * product ids) and "applies_to_product_ids" (catalog product uuids); price-
 * scoped codes (applies_to_prices metadata on the Stripe coupon/promotion
 * code — enforced server-side, Stripe can only restrict by product natively)
 * carry "applies_to_prices" (Stripe price ids) and "applies_to_price_ids"
 * (catalog price uuids). All are absent for unrestricted codes — absent means
 * the code discounts everything.
 * Unknown/inactive codes, invalid underlying coupons, Stripe errors and blank
 * codes all answer {"valid":false} with 200 — the endpoint never 5xxes for a
 * bad code. The whole discount stack fails open, so client handling mirrors
 * that: anything we could not actually verify (transport failure, malformed
 * body, unconfigured base URL) maps to "unknown" rather than "invalid", and
 * the checkout keeps its preview-inference behavior instead of calling the
 * code bad. Nothing in this module ever throws into the UI.
 */

import { PUBLIC_BILLING_API_BASE } from './config';

export const COUPON_STATUS = Object.freeze({
  valid: 'valid',
  invalid: 'invalid',
  unknown: 'unknown',
  // Validation round-trip in flight — distinct from 'unknown' (network
  // failure fall-through) so the UI can show progress instead of the
  // fail-open optimistic note while the verdict is pending.
  pending: 'pending'
});

// Shared frozen empty for the applies_to arrays on the no-amount shapes.
const NO_PRODUCTS = Object.freeze([]);

// couponInfo shapes for the two no-amount outcomes. Frozen singletons so
// consumers can share/compare them and never mutate store state.
export const UNVERIFIED_COUPON_INFO = Object.freeze({
  status: COUPON_STATUS.unknown,
  percentOff: 0,
  amountOff: 0,
  currency: '',
  duration: '',
  appliesToProducts: NO_PRODUCTS,
  appliesToProductIds: NO_PRODUCTS,
  appliesToPrices: NO_PRODUCTS,
  appliesToPriceIds: NO_PRODUCTS
});

export const INVALID_COUPON_INFO = Object.freeze({
  status: COUPON_STATUS.invalid,
  percentOff: 0,
  amountOff: 0,
  currency: '',
  duration: '',
  appliesToProducts: NO_PRODUCTS,
  appliesToProductIds: NO_PRODUCTS,
  appliesToPrices: NO_PRODUCTS,
  appliesToPriceIds: NO_PRODUCTS
});

// UI-only in-flight state: useCouponValidationSync stores this synchronously
// before the round-trip starts, and every settled outcome (valid / invalid /
// unknown) overwrites it. normalizeDiscountCodeResponse never resolves to it.
export const PENDING_COUPON_INFO = Object.freeze({
  status: COUPON_STATUS.pending,
  percentOff: 0,
  amountOff: 0,
  currency: '',
  duration: '',
  appliesToProducts: NO_PRODUCTS,
  appliesToProductIds: NO_PRODUCTS,
  appliesToPrices: NO_PRODUCTS,
  appliesToPriceIds: NO_PRODUCTS
});

/**
 * Transport-agnostic core (same shape as the marketing twin): takes a
 * `request(path, {signal})` function so tests can exercise the exact
 * error-collapsing behavior without a real fetch.
 *
 * Resolves the contract JSON on success; empty/whitespace codes short-circuit
 * to {valid:false} without a network call; transport/HTTP failures resolve to
 * {valid:false, networkError:true} — never throws.
 */
export async function fetchDiscountCodeVia(request, code, { signal } = {}) {
  const trimmed = String(code ?? '').trim();
  if (!trimmed) return { valid: false };
  try {
    const data = await request(`/public/api/v1/billing/discount-code?code=${encodeURIComponent(trimmed)}`, { signal });
    return data && typeof data === 'object' ? data : { valid: false };
  } catch {
    return { valid: false, networkError: true };
  }
}

/**
 * Maps the raw endpoint JSON (or the {valid:false, networkError:true}
 * sentinel resolved on transport failures) to the couponInfo shape stored in
 * checkout state:
 *   { status: 'valid'|'invalid'|'unknown', percentOff, amountOff, currency,
 *     duration, code? }
 */
export function normalizeDiscountCodeResponse(data) {
  if (!data || typeof data !== 'object' || data.networkError) {
    return UNVERIFIED_COUPON_INFO;
  }
  if (data.valid !== true) {
    return INVALID_COUPON_INFO;
  }
  return {
    status: COUPON_STATUS.valid,
    code: typeof data.code === 'string' ? data.code : '',
    // percent_off is a Stripe float (e.g. 33.3333); amount_off integer cents.
    percentOff: Number(data.percent_off) || 0,
    amountOff: Math.max(0, Math.round(Number(data.amount_off) || 0)),
    currency: typeof data.currency === 'string' ? data.currency.toLowerCase() : '',
    duration: typeof data.duration === 'string' ? data.duration : '',
    // Product restriction (coupon applies_to). Empty = unrestricted — the code
    // discounts every product. `appliesToProducts` carries raw Stripe product
    // ids; `appliesToProductIds` the same restriction mapped to catalog
    // product uuids (what the FE price catalog exposes), so a client can
    // scope the discount to eligible cart items.
    appliesToProducts: normalizeIdList(data.applies_to_products),
    appliesToProductIds: normalizeIdList(data.applies_to_product_ids),
    // Price restriction (applies_to_prices metadata; enforced by the backend
    // since Stripe can only restrict by product): Stripe price ids and their
    // catalog price uuids. Empty = valid for every price.
    appliesToPrices: normalizeIdList(data.applies_to_prices),
    appliesToPriceIds: normalizeIdList(data.applies_to_price_ids)
  };
}

// Coerces an applies_to array from the wire to a clean string list ([] for
// anything absent or malformed — absent means unrestricted).
function normalizeIdList(value) {
  if (!Array.isArray(value)) return [];
  return value.filter((v) => typeof v === 'string' && v !== '');
}

// Minimal REST request against the public billing base. Throws on transport
// failure, non-2xx and malformed JSON — fetchDiscountCodeVia collapses every
// throw into the networkError sentinel (⇒ status 'unknown'), so a flaky
// network or a 502 from envoy never marks a code invalid.
async function publicBillingRequest(base, path, { signal } = {}) {
  const res = await fetch(`${base}${path}`, {
    headers: { Accept: 'application/json' },
    signal
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

/**
 * Validate a typed discount code against the public endpoint and resolve the
 * normalized couponInfo. Never throws and never rejects:
 *   status 'valid'   — backend confirmed the code (amounts included)
 *   status 'invalid' — backend answered {valid:false}
 *   status 'unknown' — base URL unconfigured, transport failure, aborted
 *                      request or malformed body (fail-open)
 * The default base is the per-environment envoy gateway (src/env config) —
 * the same host that fronts gRPC-web also serves /public/api/v1/billing/*.
 * With an empty base it resolves 'unknown' without touching the network.
 */
export async function validateDiscountCode(code, { signal, baseUrl = PUBLIC_BILLING_API_BASE } = {}) {
  const base = String(baseUrl ?? '')
    .trim()
    .replace(/\/+$/, '');
  if (!base) return UNVERIFIED_COUPON_INFO;
  const data = await fetchDiscountCodeVia((path, opts) => publicBillingRequest(base, path, opts), code, { signal });
  return normalizeDiscountCodeResponse(data);
}
